import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  Inject,
  input,
  Optional,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { MatTab, MatTabChangeEvent, MatTabGroup, MatTabLabel } from '@angular/material/tabs';
import { MatTooltipModule } from '@angular/material/tooltip';
import { connectState } from '@examdojo/angular/util';
import {
  IMAGE_CHAT,
  ImageChatComponent,
  provideChatScrollTrigger,
  provideImageChatCodeGenerationService,
} from '@examdojo/chat';
import { ButtonComponent } from '@examdojo/core/button';
import { IconButtonComponent } from '@examdojo/core/icon-button';
import { KeyboardBinding, PythonEditorComponent } from '@examdojo/core/python-editor';
import { SingleSelectComponent } from '@examdojo/core/select';
import { ToastService } from '@examdojo/core/toast';
import { isNotNullish } from '@examdojo/core/util/nullish';
import { ContentItemEditingOptions, ImageEditingType } from '@examdojo/models/question-editor';
import {
  ImageContent,
  isLocalQuestionImageUrl,
  isRemoteQuestionImageUrl,
  QuestionImage,
} from '@examdojo/models/question-image';
import { ImageDropzoneComponent } from '@examdojo/ui/image-dropzone';
import { ImagePreviewDirective } from '@examdojo/ui/image-preview';
import { NumberInputComponent } from '@examdojo/ui/input';
import { MarkdownViewerComponent } from '@examdojo/ui/markdown-viewer';
import { MultiLineTextComponent } from '@examdojo/ui/multi-line-text';
import { AngularSplitModule } from 'angular-split';
import { KeyCode, KeyMod } from 'monaco-editor';
import { filter, finalize, map, merge, of, startWith, switchMap, tap } from 'rxjs';
import { QUESTION_IMAGE_BUCKET_NAME, QUESTION_IMAGE_BUCKET_NAME_DEFAULT } from '../models/image-bucket-name.model';
import { QuestionImageCacheService } from '../services/question-image-cache.service';
import { QuestionImageGenerationService } from '../services/question-image-generation.service';
import { QuestionImageUploadService } from '../services/question-image-upload.service';
import { QuestionImageChatCodeGenerationService } from './image-chat-generate-code.service';
import { ImageChatScrollProvider } from './image-chat-scroll-provider';

@Component({
  selector: 'dojo-question-image-editor',
  templateUrl: './question-image-editor.component.html',
  imports: [
    ReactiveFormsModule,
    MarkdownViewerComponent,
    IconButtonComponent,
    ImageDropzoneComponent,
    MatTooltipModule,
    MatTabGroup,
    MatTab,
    MatTabLabel,
    ImagePreviewDirective,
    ButtonComponent,
    PythonEditorComponent,
    MatSlideToggle,
    MultiLineTextComponent,
    NumberInputComponent,
    SingleSelectComponent,
    ImageChatComponent,
    AngularSplitModule,
  ],
  styleUrls: ['./question-image-editor.component.scss'],
  providers: [
    QuestionImageGenerationService,
    provideChatScrollTrigger(ImageChatScrollProvider),
    provideImageChatCodeGenerationService(QuestionImageChatCodeGenerationService),
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'flex flex-col h-full overflow-hidden',
  },
  styles: [
    `
      .badge-above {
        @apply rounded px-2 py-1 text-xs font-medium tracking-wide;
      }
    `,
  ],
})
export class QuestionImageEditorComponent {
  constructor(
    private readonly questionImageGenerationService: QuestionImageGenerationService,
    private readonly questionImageUploadService: QuestionImageUploadService,
    private readonly questionImageCacheService: QuestionImageCacheService,
    @Optional() @Inject(QUESTION_IMAGE_BUCKET_NAME) private readonly bucketName: string | null,
    private readonly destroyRef: DestroyRef,
    private readonly toastService: ToastService,
    private readonly imageChatScrollProvider: ImageChatScrollProvider,
    private readonly questionImageChatCodeGenerationService: QuestionImageChatCodeGenerationService,
  ) {
    merge(
      this.setupFormControlSyncSubscriptions(),
      this.updateComponentBasedOnFormCtrlValue(),
      this.applyCodeAndImageFromChat(),
    )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
  }

  readonly imageChat = inject(IMAGE_CHAT, { optional: true });

  readonly formCtrl = input.required<FormControl<QuestionImage>>();
  readonly fillInTheBlanksFormCtrl = input<FormControl<boolean | undefined>>();
  readonly editingOptions = input<ContentItemEditingOptions>({
    allowTextEditing: true,
    allowImageEditing: [ImageEditingType.Original, ImageEditingType.Generated],
    allowFillInTheBlanks: true,
  });
  readonly contentJsonPath = input.required<string>();

  // Computed signals for tab accessibility
  readonly canEditOriginal = computed(() =>
    this.editingOptions().allowImageEditing.includes(ImageEditingType.Original),
  );
  readonly canEditGenerated = computed(() =>
    this.editingOptions().allowImageEditing.includes(ImageEditingType.Generated),
  );

  // Hide tab header when only one tab is visible
  readonly shouldHideTabHeader = computed(() => !this.canEditGenerated());

  // New form controls for the three properties
  readonly titleFormCtrl = new FormControl<string>('');

  readonly notToScaleFormCtrl = new FormControl<boolean>(false, {
    nonNullable: true,
  });

  readonly heightFormCtrl = new FormControl<number | null>(null);

  readonly positionFormCtrl = new FormControl<string>('center', {
    nonNullable: true,
  });

  readonly positionOptions = [
    { value: 'left', label: 'Align Left' },
    { value: 'center', label: 'Center' },
    { value: 'right', label: 'Align Right' },
  ];

  readonly containerLayoutFormCtrl = new FormControl<string>('100%', {
    nonNullable: true,
  });

  readonly containerLayoutOptions = [
    { value: '100%', label: '100%' },
    { value: '75%', label: '75%' },
    { value: '50%', label: '50%' },
    { value: '25%', label: '25%' },
  ];

  readonly originalImage = signal<File | null>(null);
  readonly originalImageBlobUrl = computed(() => {
    const selectedImage = this.originalImage();
    return selectedImage ? URL.createObjectURL(selectedImage) : '';
  });

  readonly codeFormCtrl = new FormControl<string>('', {
    nonNullable: true,
  });
  readonly imageFromCodeLoading = signal(false);
  readonly imageFromCodeMessage = signal<string | null>(null);
  readonly imageFromCode = signal<File | null>(null);
  readonly imageFromCodeBlobUrl = computed(() => {
    const image = this.imageFromCode();
    return image ? URL.createObjectURL(image) : '';
  });

  readonly manuallyUploadedImage = signal<File | null>(null);
  readonly manuallyUploadedImageBlobUrl = computed(() => {
    const selectedImage = this.manuallyUploadedImage();
    return selectedImage ? URL.createObjectURL(selectedImage) : '';
  });

  readonly formCtrlValue$ = toObservable(this.formCtrl).pipe(
    switchMap((formCtrl) => formCtrl.valueChanges.pipe(startWith(formCtrl.value))),
  );

  readonly isCodeFormCtrlValueValid = this.codeFormCtrl.valueChanges.pipe(map((value) => !!value.trim().length));

  readonly originalSignedUrl$ = this.formCtrlValue$.pipe(
    filter(isNotNullish),
    switchMap((formCtrlValue) => {
      if (formCtrlValue.content?.original_url && isRemoteQuestionImageUrl(formCtrlValue.content.original_url)) {
        return this.questionImageUploadService.fetchSignedUrlFromImageUrl(formCtrlValue.content.original_url);
      }

      // For local URLs, check if we have a cached file
      if (formCtrlValue.content?.original_url && isLocalQuestionImageUrl(formCtrlValue.content.original_url)) {
        const file = this.questionImageCacheService.getLocalImage(formCtrlValue.content.original_url);
        return of(file ? URL.createObjectURL(file) : null);
      }

      return of(null);
    }),
  );

  private readonly generateImageShortCutId = 'ctrlKeyY';

  readonly keyboardBindings: KeyboardBinding[] = [
    {
      id: this.generateImageShortCutId,
      keybindings: [KeyMod.CtrlCmd | KeyCode.KeyY],
    },
  ];

  readonly state = connectState({
    formCtrlValue: this.formCtrlValue$,
    originalSignedUrl: this.originalSignedUrl$,
    isCodeFormCtrlValueValid: this.isCodeFormCtrlValueValid,
  });

  async setManuallyUploadedImageToFormCtrl(image: File | null) {
    this.manuallyUploadedImage.set(image);
    this.updateFormCtrlFile(image);
  }

  async setOriginalImageToFormCtrl(image: File | null) {
    this.originalImage.set(image);
    this.updateOriginalImageFormCtrl(image);
  }

  generateNewImage() {
    const imageCode = this.codeFormCtrl.value.trim();

    if (!imageCode || this.imageFromCodeLoading()) {
      return;
    }

    this.imageFromCodeLoading.set(true);
    this.imageFromCodeMessage.set(null);

    this.questionImageGenerationService
      .generateImage(imageCode)
      .pipe(
        tap((image) => {
          this.imageFromCode.set(image);
          this.updateFormCtrlFile(image, imageCode);
        }),
        tap(() => {
          this.toastService.success('image generated');
        }),
        finalize(() => {
          this.imageFromCodeLoading.set(false);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  onKeyBoardEvents(shortCutId: string) {
    if (shortCutId === this.generateImageShortCutId) {
      this.generateNewImage();
    }
  }

  onTabChange(event: MatTabChangeEvent) {
    // chat tab is forth tab
    if (event.index === 3) {
      this.imageChatScrollProvider.scrollChat();
    }
  }

  private setupFormControlSyncSubscriptions() {
    // These subscriptions handle syncing FROM child form controls TO the main form
    // They use FormControl.valueChanges observables which trigger when the user interacts with UI elements

    return merge(
      // Sync title changes
      this.titleFormCtrl.valueChanges.pipe(
        tap((titleValue) => {
          this.updateMainFormField('title', titleValue || null);
        }),
      ),

      // Sync not-to-scale changes
      this.notToScaleFormCtrl.valueChanges.pipe(
        tap((notToScaleValue) => {
          this.updateMainFormField('not_to_scale', notToScaleValue);
        }),
      ),

      // Sync height, position, and width changes to create combined style
      this.heightFormCtrl.valueChanges.pipe(
        tap((heightValue) => {
          // Use the emitted height value along with current position to update style
          const currentPosition = this.positionFormCtrl.value;
          this.updateStyleFromValues(heightValue, currentPosition);
        }),
      ),

      this.positionFormCtrl.valueChanges.pipe(
        tap((positionValue) => {
          // Use the emitted position value along with current height to update style
          const currentHeight = this.heightFormCtrl.value;
          this.updateStyleFromValues(currentHeight, positionValue);
        }),
      ),

      this.containerLayoutFormCtrl.valueChanges.pipe(
        tap((containerLayoutValue) => {
          // Update the container_width property directly
          this.updateMainFormField('container_width', containerLayoutValue);
        }),
      ),
    );
  }

  private updateMainFormField<K extends keyof QuestionImage>(field: K, value: QuestionImage[K] | null) {
    const newValue = {
      ...this.formCtrl().value,
      [field]: value,
    };

    this.formCtrl().setValue(newValue);
    this.formCtrl().markAsDirty();
    this.formCtrl().markAsTouched();
  }

  private updateContentField<K extends keyof ImageContent>(field: K, value: ImageContent[K]) {
    const currentValue = this.formCtrl().value;
    const newValue = {
      ...currentValue,
      content: {
        ...currentValue.content,
        [field]: value,
      },
    };
    this.formCtrl().setValue(newValue);
    this.formCtrl().markAsDirty();
    this.formCtrl().markAsTouched();
  }

  private updateStyleFromValues(height: number | null, position: string) {
    const style: Record<string, string> = {};

    // Add height if specified
    if (height && height > 0) {
      style['height'] = `${height}px`;
    }

    // Add position styling - use justify-content for image positioning within its container
    if (position === 'left') {
      style['justify-content'] = 'flex-start';
    } else if (position === 'right') {
      style['justify-content'] = 'flex-end';
    } else if (position === 'center') {
      style['justify-content'] = 'center';
    }

    // If no meaningful style, set to undefined
    const finalStyle = Object.keys(style).length > 0 ? style : undefined;

    this.updateMainFormField('style', finalStyle);
  }

  private updateFormCtrlFile(file: File | null, imageCode?: string) {
    const currentValue = this.formCtrl().value;
    const currentUrl = currentValue.content?.url;

    if (!file && currentUrl) {
      if (isLocalQuestionImageUrl(currentUrl)) {
        this.questionImageCacheService.removeLocalImage(currentUrl);
        this.updateContentField('url', null);
      }

      return;
    }

    if (!file) {
      return;
    }

    const localFileUrl = this.questionImageCacheService.setLocalImage(
      file,
      currentValue.content?.url,
      this.bucketName ?? QUESTION_IMAGE_BUCKET_NAME_DEFAULT,
    );

    this.updateContentField('url', localFileUrl);
    if (imageCode) {
      this.updateContentField('code', imageCode);
    }
  }

  private updateOriginalImageFormCtrl(file: File | null) {
    const currentValue = this.formCtrl().value;
    const currentOriginalUrl = currentValue.content?.original_url;

    if (!file && currentOriginalUrl) {
      // Remove local image from cache if it's a local URL
      if (isLocalQuestionImageUrl(currentOriginalUrl)) {
        this.questionImageCacheService.removeLocalImage(currentOriginalUrl);
      }

      // Clear the originalImage signal as well
      this.originalImage.set(null);

      // Always clear the original_url regardless of its type
      this.updateContentField('original_url', null);
      return;
    }

    if (!file) {
      return;
    }

    const localFileUrl = this.questionImageCacheService.setLocalImage(
      file,
      currentValue.content?.original_url ?? null,
      this.bucketName ?? QUESTION_IMAGE_BUCKET_NAME_DEFAULT,
    );

    this.updateContentField('original_url', localFileUrl);
  }

  private updateComponentBasedOnFormCtrlValue() {
    return this.formCtrlValue$.pipe(
      tap((formCtrlValue) => {
        this.codeFormCtrl.setValue(formCtrlValue.content?.code ?? '', { emitEvent: false });
        this.titleFormCtrl.setValue(formCtrlValue.title ?? '', { emitEvent: false });
        this.notToScaleFormCtrl.setValue(formCtrlValue.not_to_scale ?? false, { emitEvent: false });

        const style = formCtrlValue.style;
        if (style) {
          const heightValue = style.height ? parseInt(style.height.replace('px', '')) : null;
          this.heightFormCtrl.setValue(heightValue, { emitEvent: false });

          // Extract position from justify-content
          let position = 'center';
          const justifyContent = (style as Record<string, string>)['justify-content'];

          if (justifyContent === 'flex-start') {
            position = 'left';
          } else if (justifyContent === 'flex-end') {
            position = 'right';
          } else if (justifyContent === 'center') {
            position = 'center';
          }

          this.positionFormCtrl.setValue(position, { emitEvent: false });
        } else {
          this.heightFormCtrl.setValue(null, { emitEvent: false });
          this.positionFormCtrl.setValue('center', { emitEvent: false });
        }

        // Extract container layout from the dedicated property
        const containerLayout = formCtrlValue.container_width || '100%';
        this.containerLayoutFormCtrl.setValue(containerLayout, { emitEvent: false });
      }),
    );
  }

  private applyCodeAndImageFromChat() {
    return this.questionImageChatCodeGenerationService.chatImageAndCodeApplied$.pipe(
      tap(({ image, code }) => {
        this.updateFormCtrlFile(image, code);
      }),
    );
  }
}
